import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE_URL from '../utils/apiConfig'; // Ensure this path is correct
import { useAuth } from '../contexts/AuthContext';
import { jobRoles, jobLocations } from '../utils/constants'; // Ensure this path is correct
import './PostJobModal.css'; // You'll need to create this CSS file

// Helper component for required field asterisk
const Req = () => <span className="required">*</span>;

const PostJobModal = ({ onClose, initialJobData, isEditing }) => {
    const { firebaseUser } = useAuth();

    // Initialize state with default or initial data
    const [jobType, setJobType] = useState(initialJobData?.jobType || 'private');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        jobRole: '', title: '', companyName: '', qualification: '', vacancies: '',
        experience: '', locationState: '', locationDistrict: '', description: '',
        age: '', salary: '', contactDetails: '', applyLink: '', lastDate: '',
        eligibility: '', moreInfoLink: ''
    });

    // Populate form data when the modal opens or initialJobData changes
    useEffect(() => {
        if (isEditing && initialJobData) {
            // Map initial data to form state, filling in missing fields with defaults
            setFormData(prev => ({
                ...prev,
                ...initialJobData
            }));
            setJobType(initialJobData.jobType);
        }
    }, [isEditing, initialJobData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
        const idToken = await firebaseUser.getIdToken(true);

        if (isEditing) {
            // --- CASE 1: EDITING (Always Free) ---
            await axios.put(`${API_BASE_URL}/jobs/${initialJobData.id}`, formData, {
                headers: { Authorization: `Bearer ${idToken}` }
            });
            setSuccess('Job details updated successfully!');
            setTimeout(onClose, 2000);
        } else {
            // PATH 2: NEW POST (First free, then Paid)
            try {
                const response = await axios.post(`${API_BASE_URL}/jobs`, { ...formData, jobType }, {
                    headers: { Authorization: `Bearer ${idToken}` }
                });
                
                // If the code reaches here, it was FREE and successful
                setSuccess('Job posted successfully!');
                setTimeout(onClose, 2000);

            } catch (err) {
                // Check if the server responded with 403 and the specific error code
                const serverErrorCode = err.response?.data?.errorCode;
                const serverMessage = err.response?.data?.message;

                console.log("Server Response Error Code:", serverErrorCode);
                console.log("Server Response Message:", serverMessage);

                if (serverErrorCode === "PAYMENT_REQUIRED") {
                    setLoading(false); // Stop spinner so user can see the confirm box
                    
                    const proceed = window.confirm(
                        "Note: You have already used your one-time free job post.\n\nWould you like to pay ₹100 to post this job?"
                    );

                    if (proceed) {
                        handleNewPaidJobPayment(); // Initiate Razorpay
                    } else {
                        setError("Payment required to post additional private jobs.");
                    }
                } else {
                    // It's a different error (like missing fields)
                    setError(serverMessage || "Failed to post job. Please check all fields.");
                }
            }
        }
    } catch (err) {
        console.error("Outer Submit Error:", err);
        setError("An unexpected error occurred. Please try again.");
    } finally {
        setLoading(false);
    }
};

    const isStateWithDistricts = formData.locationState && jobLocations[formData.locationState]?.length > 0;

    const handleNewPaidJobPayment = async () => {
    setLoading(true);
    try {
        const idToken = await firebaseUser.getIdToken(true);

        // 1. Create the Razorpay Order
        const orderRes = await axios.post(
            `${API_BASE_URL}/repost-job/create-order`, 
            {}, 
            { headers: { Authorization: `Bearer ${idToken}` } }
        );

        const order = orderRes.data;

        // 2. Open Razorpay Checkout
        const options = {
            key: "rzp_test_RR0L9EVjK2DOXO",
            amount: order.amount,
            currency: "INR",
            name: "AiNews",
            description: "Private Job Posting Fee",
            order_id: order.id,
            handler: async (response) => {
                // 3. This runs if payment is successful
                try {
                    await axios.post(
                        `${API_BASE_URL}/verify-new-paid-job`,
                        {
                            razorpay_order_id: response.razorpay_order_id,
                            razorpay_payment_id: response.razorpay_payment_id,
                            razorpay_signature: response.razorpay_signature,
                            jobData: { ...formData, jobType: 'private' } // Send the form data to be saved
                        },
                        { headers: { Authorization: `Bearer ${idToken}` } }
                    );
                    setSuccess("Payment successful! Job posted.");
                    setTimeout(onClose, 2000);
                } catch (verifyErr) {
                    setError("Payment verified, but failed to save job. Contact support.");
                }
            },
            prefill: {
                name: firebaseUser.displayName || "",
                email: firebaseUser.email || "",
            },
            theme: { color: "#488AFF" },
            modal: {
                ondismiss: () => setLoading(false)
            }
        };

        const rzp = new window.Razorpay(options);
        rzp.open();

    } catch (err) {
        console.error("Error initiating payment", err);
        setError("Could not initiate payment. Please try again.");
        setLoading(false);
    }
};

    return (
        <div className="modal-backdrop" onClick={onClose}>
            <div className="modal-content post-job-modal" onClick={e => e.stopPropagation()}>
                <h2>{isEditing ? 'Edit Job Post' : 'Post a Job'}</h2>
                <button className="modal-close-button" onClick={onClose}>&times;</button>
                
                <div className="job-type-selector">
                    <label><input type="radio" name="jobType" value="private" checked={jobType === 'private'} onChange={(e) => setJobType(e.target.value)} /> Private Job</label>
                    <label><input type="radio" name="jobType" value="government" checked={jobType === 'government'} onChange={(e) => setJobType(e.target.value)} /> Government Job</label>
                </div>

                {error && <p className="error-message">{error}</p>}
                {success && <p className="success-message">{success}</p>}

                <form onSubmit={handleSubmit} className="post-job-form">
                    {/* Private Job Fields */}
                    {jobType === 'private' && (
                        <>
                            <div className="form-group"><label>Job Role <Req /></label><select name="jobRole" value={formData.jobRole} onChange={handleChange} required><option value="">Select Role...</option>{jobRoles.map(r => <option key={r} value={r}>{r}</option>)}</select></div>
                            <div className="form-group"><label>Title <Req /></label><input type="text" name="title" value={formData.title} onChange={handleChange} required /></div>
                            <div className="form-group"><label>Company Name <Req /></label><input type="text" name="companyName" value={formData.companyName} onChange={handleChange} required /></div>
                            <div className="form-row">
                                <div className="form-group"><label>Qualification <Req /></label><input type="text" name="qualification" value={formData.qualification} onChange={handleChange} required /></div>
                                <div className="form-group"><label>Vacancies <Req /></label><input type="number" name="vacancies" value={formData.vacancies} onChange={handleChange} required /></div>
                            </div>
                            <div className="form-group"><label>Experience <Req /></label><input type="text" name="experience" value={formData.experience} onChange={handleChange} required /></div>
                            <div className="form-row">
                                <div className="form-group"><label>Job Location <Req /></label><select name="locationState" value={formData.locationState} onChange={handleChange} required><option value="">Select State...</option>{Object.keys(jobLocations).map(l => <option key={l} value={l}>{l}</option>)}</select></div>
                                {isStateWithDistricts && <div className="form-group"><label>District (Optional)</label><select name="locationDistrict" value={formData.locationDistrict} onChange={handleChange}><option value="">All Districts</option>{jobLocations[formData.locationState].map(d => <option key={d} value={d}>{d}</option>)}</select></div>}
                            </div>
                            <div className="form-group"><label>Description (Optional)</label><textarea name="description" value={formData.description} onChange={handleChange} rows="4"></textarea></div>
                            <div className="form-row">
                                <div className="form-group"><label>Age (Optional)</label><input type="text" name="age" value={formData.age} onChange={handleChange} /></div>
                                <div className="form-group"><label>Salary <Req /></label><input type="text" name="salary" value={formData.salary} onChange={handleChange} required /></div>
                            </div>
                            <div className="form-group"><label>Contact Details <Req /></label><input type="text" name="contactDetails" value={formData.contactDetails} onChange={handleChange} required /></div>
                            <div className="form-row">
                                <div className="form-group"><label>Apply Link (Optional)</label><input type="url" name="applyLink" value={formData.applyLink} onChange={handleChange} /></div>
                                <div className="form-group"><label>Last Date (Optional)</label><input type="date" name="lastDate" value={formData.lastDate} onChange={handleChange} /></div>
                            </div>
                        </>
                    )}
                    {/* Government Job Fields */}
                    {jobType === 'government' && (
                        <>
                            <div className="form-group"><label>Title <Req /></label><input type="text" name="title" value={formData.title} onChange={handleChange} required /></div>
                            <div className="form-group"><label>Eligibility <Req /></label><input type="text" name="eligibility" value={formData.eligibility} onChange={handleChange} required /></div>
                            <div className="form-group"><label>Vacancies <Req /></label><input type="number" name="vacancies" value={formData.vacancies} onChange={handleChange} required /></div>
                            <div className="form-group"><label>Salary/Stipend <Req /></label><input type="text" name="salary" value={formData.salary} onChange={handleChange} required /></div>
                            <div className="form-group"><label>Last Date <Req /></label><input type="date" name="lastDate" value={formData.lastDate} onChange={handleChange} required /></div>
                            <div className="form-group"><label>Apply Link <Req /></label><input type="url" name="applyLink" value={formData.applyLink} onChange={handleChange} required /></div>
                            <div className="form-group"><label>More Info Link (Optional)</label><input type="url" name="moreInfoLink" value={formData.moreInfoLink} onChange={handleChange} /></div>
                        </>
                    )}
                    <div className="submit-btn-container">
                        <button type="submit" className="submit-btn" disabled={loading}>
                            {loading ? 'Processing...' : isEditing ? 'Update Job Post' : 'Submit Job Post'}
                        </button>
                        {!isEditing && jobType === 'private' && (
                            <p className="payment-warning-text">
                            * Private job posts are valid for 30 days. Payments are non-refundable.
                            </p>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
};

export default PostJobModal;